import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Toggle, Checkbox } from '../../../../components';
import { selectors as customerConfigurationsSelectors, actions as customerConfigurationsActions } from '../../../../redux/customerConfigurations';
import { makePatch } from '../../../../api/ajax';

import './sass/mfa-required-modal.scss';

const MfaRequiredModal = ({ show, onModalClose }) => {
  const customerConfiguration = useSelector(customerConfigurationsSelectors.selectCustomerConfiguration);
  const dispatch = useDispatch();
  const [apiUserConfirmed, setApiUserConfirmed] = useState(customerConfiguration?.mfaEnabled || false);
  const [emailAccessConfirmed, setEmailAccessConfirmed] = useState(customerConfiguration?.mfaEnabled || false);

  if (!customerConfiguration) return null;

  const isMfaEnabled = customerConfiguration.mfaEnabled;
  const areCheckboxesChecked = apiUserConfirmed && emailAccessConfirmed;

  const onToggleClick = () => {
    if (!isMfaEnabled && !areCheckboxesChecked) {
      return; // Prevent enabling MFA if checkboxes are not checked
    }

    const updateCustomerConfigDto = {
      mfaEnabled: !isMfaEnabled
    };

    makePatch('customerConfigurations/current', updateCustomerConfigDto).subscribe(
      ({ response }) => {
        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
        // Reset checkboxes when MFA is disabled
        if (isMfaEnabled) {
          setApiUserConfirmed(false);
          setEmailAccessConfirmed(false);
        }
      }
    );
  };

  const handleApiUserCheckbox = ({ value }) => {
    setApiUserConfirmed(!value);
  };

  const handleEmailAccessCheckbox = ({ value }) => {
    setEmailAccessConfirmed(!value);
  };

  return (
    <Modal
      headerText="Enable Multi-Factor Authentication"
      size="md"
      backdrop="static"
      onHide={() => onModalClose(false)}
      show={show}
    >
      <Modal.Body className="mfa-required-modal__body">
        <div className="mfa-required-modal__description">
          <p>
            Multi-factor authentication adds an extra layer of security by requiring users to verify new device logins
            with a verification code sent to their email. Before enabling this feature, please complete the setup steps below.
          </p>
        </div>

        <div className="mfa-required-modal__section">
          <h5 className="mfa-required-modal__section-title">1. API User Setup</h5>
          <div className="mfa-required-modal__instructions">
            <p className="mfa-required-modal__instruction-step">
              <strong>1.1</strong> Ensure your DriveX integration authenticates with an &quot;API user&quot; account
            </p>
            <p className="mfa-required-modal__instruction-step">
              <strong>1.2</strong> If the API and Admin previously shared the same account, create a new admin user
              (with &quot;User&quot; or &quot;Moderator&quot; role) for DriveX Admin
            </p>
          </div>
          <Checkbox
            id="api-user-checkbox"
            name="apiUserConfirmed"
            label="I confirm my API authentication uses an &quot;API user&quot; account"
            checked={apiUserConfirmed}
            onClickHandler={handleApiUserCheckbox}
            disabled={isMfaEnabled}
          />
        </div>

        <div className="mfa-required-modal__section">
          <h5 className="mfa-required-modal__section-title">2. Email Verification Setup</h5>
          <div className="mfa-required-modal__instructions">
            <p className="mfa-required-modal__instruction-step">
              <strong>2.1</strong> Ensure all DriveX Admin users have valid email addresses where they can receive
              verification codes
            </p>
            <p className="mfa-required-modal__instruction-step">
              <strong>2.2</strong> Test that users can receive emails at their registered email addresses
            </p>
          </div>
          <Checkbox
            id="email-access-checkbox"
            name="emailAccessConfirmed"
            label="I confirm that all users can receive verification codes at their registered email addresses"
            checked={emailAccessConfirmed}
            onClickHandler={handleEmailAccessCheckbox}
            disabled={isMfaEnabled}
          />
        </div>

        <div className="mfa-required-modal__toggle-section">
          <div className="mfa-required-modal__toggle-container">
            <div>
              <div className="mfa-required-modal__toggle-label">
                Require multi-factor authentication for all users
              </div>
              <div className="mfa-required-modal__toggle-hint">
                {isMfaEnabled
                  ? 'MFA is currently enabled'
                  : areCheckboxesChecked
                    ? 'Ready to enable'
                    : 'Complete the steps above to enable'}
              </div>
            </div>
            <Toggle
              on={isMfaEnabled}
              toggleClick={onToggleClick}
              disabled={!isMfaEnabled && !areCheckboxesChecked}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

MfaRequiredModal.propTypes = {
  show: PropTypes.bool,
  onModalClose: PropTypes.func,
};

export default MfaRequiredModal;
